import { DeviceTelemetry } from './types';

export class MockBleDevice {
  public isConnected = false;
  public deviceName = 'FP102-SIM994';
  public serialNumber = '10225210014';
  public model: 'FP102' | 'FV801' = 'FP102';
  public firmwareVersion = 'V1.1.006';
  public batteryPercent = 94;
  public rssi = -68;

  public countInterval = 1000;
  public serverHost = 'op.foorir.cn';
  public serverPort = 8188;
  public serverApi = '/api/infrared/V1/dataUpload';

  public apn = 'iot.1nce.net';
  public apnUser = '';
  public apnPass = '';
  public imei = '864982050123456';
  public simId = '8941012345678901234F';
  public simInserted = true;
  public netConnected = true;
  public serverStatus = 'onLine';
  public inCount = 142;
  public outCount = 138;
  public totalCount = 280;
  public runLog = false;

  public setModel(m: 'FP102' | 'FV801') {
    this.model = m;
    if (m === 'FV801') {
      this.deviceName = 'FV801-SIM994';
      this.serialNumber = '80125210014';
    } else {
      this.deviceName = 'FP102-SIM994';
      this.serialNumber = '10225210014';
    }
  }

  private onNotifyCallback?: (data: Uint8Array) => void;
  private onDisconnectCallback?: () => void;
  private upgradeTotalLen = 0;
  private upgradeReceivedLen = 0;
  private pollCount = 0;

  public setNotifyCallback(cb: (data: Uint8Array) => void) {
    this.onNotifyCallback = cb;
  }

  public setDisconnectCallback(cb: () => void) {
    this.onDisconnectCallback = cb;
  }

  public async connect(): Promise<DeviceTelemetry> {
    await new Promise((r) => setTimeout(r, 400)); // simulated connection delay
    this.isConnected = true;
    this.pollCount = 0;
    return this.getTelemetry();
  }

  public disconnect() {
    const wasConnected = this.isConnected;
    this.isConnected = false;
    if (wasConnected && this.onDisconnectCallback) {
      this.onDisconnectCallback();
    }
  }

  public getTelemetry(): DeviceTelemetry {
    return {
      model: this.model,
      deviceName: this.deviceName,
      serialNumber: this.serialNumber,
      firmwareVersion: this.firmwareVersion,
      batteryPercent: this.batteryPercent,
      batteryVoltage: 3.62,
      rssi: this.rssi,
      countingMode: this.model === 'FV801' ? 'Omni-Directional (Total)' : 'twoWay (In / Out)',
      direction: this.model === 'FV801' ? 'total' : 'twoWay',
      countInterval: this.countInterval,
      totalCount: this.totalCount,
      serverHost: this.serverHost,
      serverPort: this.serverPort,
      serverApi: this.serverApi,
      apn: this.apn,
      apnUser: this.apnUser,
      apnPass: this.apnPass,
      imei: this.imei,
      simId: this.simId,
      simInserted: this.simInserted,
      netConnected: this.netConnected,
      serverStatus: this.serverStatus,
      isConnected: this.isConnected
    };
  }

  public async write(data: Uint8Array): Promise<void> {
    if (!this.isConnected) throw new Error('Mock device not connected');

    // Check if this is a JSON command
    const str = new TextDecoder().decode(data);
    if (str.trim().startsWith('{')) {
      try {
        const obj = JSON.parse(str.trim());
        await this.handleJsonCommand(obj);
        return;
      } catch {
        // Fall through to binary handling
      }
    }

    // Binary chunk during DFU
    if (this.upgradeTotalLen > 0) {
      this.upgradeReceivedLen += data.byteLength;
      // Simulated flash page write delay (8-15ms)
      await new Promise((r) => setTimeout(r, 12));

      // Device notifies rx_len of the chunk received (e.g. 128 bytes)
      const resp = `{"rx_len":${data.byteLength}}\r\n`;
      this.sendNotification(resp);

      // Check if finished
      if (this.upgradeReceivedLen >= this.upgradeTotalLen) {
        await new Promise((r) => setTimeout(r, 200));
        this.sendNotification(`{"upgrade_index":${this.upgradeTotalLen},"upgrade_len":${this.upgradeTotalLen}}\r\n`);
        await new Promise((r) => setTimeout(r, 100));
        this.sendNotification(`{"info":"upgrade","state":"ok"}\r\n`);
        
        // Simulate reboot with new firmware version and BLE disconnect after 600ms
        setTimeout(() => {
          this.firmwareVersion = 'V1.1.007';
          this.disconnect();
        }, 600);
      }
    }
  }

  private async handleJsonCommand(obj: Record<string, unknown>) {
    if (obj.info === 'upgrade' && typeof obj.len === 'number') {
      this.upgradeTotalLen = obj.len;
      this.upgradeReceivedLen = 0;
      // Simulate erase delay (45 pages ~ 400ms)
      await new Promise((r) => setTimeout(r, 450));
      this.sendNotification(`{"info":"upgrade","state":"begin"}\r\n`);
      return;
    }

    if (obj.info === 'get' || obj.cmd === 'get_info' || obj.info === 'status') {
      await new Promise((r) => setTimeout(r, 80));
      // Formatted exactly like STM32 foorir_bt.c
      const payload: Record<string, unknown> = {
        id: this.serialNumber,
        time: '20260929181500',
        Bat: this.batteryPercent,
        ver: this.firmwareVersion,
        model: this.model,
        open: '0800',
        close: '2000',
        storCyc: 5,
        upCyc: 60,
        addr: this.serverHost,
        port: this.serverPort,
        api: this.serverApi,
        netS: this.netConnected,
        serverS: this.serverStatus,
        apn: this.apn,
        apnN: this.apnUser,
        apnP: this.apnPass,
        SIM: this.simInserted,
        SIMID: this.simId,
        IMEI: this.imei,
        rssi: this.rssi,
        dir: this.model === 'FV801' ? 'total' : 'twoWay'
      };
      if (this.model === 'FV801') {
        payload.countInterval = this.countInterval;
      }
      this.sendNotification(JSON.stringify(payload) + '\r\n');
      return;
    }

    if (obj.countInterval !== undefined) {
      this.countInterval = Number(obj.countInterval);
      await new Promise((r) => setTimeout(r, 120));
      this.sendNotification(`{"countInterval":${this.countInterval}}\r\n`);
      return;
    }

    if (obj.apn !== undefined) {
      this.apn = String(obj.apn);
      if (obj.apnN !== undefined) this.apnUser = String(obj.apnN);
      if (obj.apnP !== undefined) this.apnPass = String(obj.apnP);
      await new Promise((r) => setTimeout(r, 150));
      this.sendNotification(`{"apn":"${this.apn}","apnN":"${this.apnUser}","apnP":"${this.apnPass}"}\r\n`);
      return;
    }

    if (obj.addr && obj.port) {
      this.serverHost = String(obj.addr);
      this.serverPort = Number(obj.port);
      if (obj.api) this.serverApi = String(obj.api);
      await new Promise((r) => setTimeout(r, 150));
      this.sendNotification(`{"config":"ok","addr":"${this.serverHost}","port":${this.serverPort}}\r\n`);
      return;
    }

    if (obj.currentData === 'get') {
      await new Promise((r) => setTimeout(r, 60));
      this.pollCount++;
      if (this.model === 'FV801') {
        if (this.pollCount > 1 && Math.random() < 0.72) {
          this.totalCount += 1;
        }
        this.sendNotification(`{"total":${this.totalCount}}\r\n`);
        return;
      }

      // In Hardware Simulator mode, simulate live passage events during continuous polling (pollCount > 1):
      // Keep static on initial connection query (pollCount === 1), but dynamically count up when Live Polling is active.
      if (this.pollCount > 1) {
        const roll = Math.random();
        if (roll < 0.42) {
          this.inCount += 1;
        } else if (roll < 0.74) {
          this.outCount += 1;
        } else if (roll < 0.86) {
          this.inCount += 1;
          this.outCount += 1;
        }
        // (~14% quiet interval with no change for realistic organic flow)
      }
      this.sendNotification(`{"in":${this.inCount},"out":${this.outCount}}\r\n`);
      return;
    }

    if (obj.clearDay === '1') {
      this.inCount = 0;
      this.outCount = 0;
      this.totalCount = 0;
      this.pollCount = 1; // Allows the very next live poll to immediately start counting from 0
      await new Promise((r) => setTimeout(r, 80));
      this.sendNotification('{"clearDay":"1"}\r\n');
      return;
    }

    if (obj.restart === '1') {
      this.sendNotification('{"restart":"1"}\r\n');
      setTimeout(() => {
        this.disconnect();
      }, 350);
      return;
    }

    if (obj.runLog === 'on') {
      this.runLog = true;
      this.sendNotification('{"runLog":"on"}\r\n');
      return;
    }

    if (obj.runLog === 'off') {
      this.runLog = false;
      this.sendNotification('{"runLog":"off"}\r\n');
      return;
    }

    if (obj.upload === 'begin') {
      this.sendNotification('{"upload":"begin"}\r\n');
      setTimeout(() => this.sendNotification('Turn on the network module.\r\n'), 200);
      setTimeout(() => this.sendNotification('wait for response.\r\n'), 600);
      setTimeout(() => this.sendNotification('MCU_TO_CAT1_CMD: AT\r\n'), 1000);
      setTimeout(() => this.sendNotification('MCU_TO_CAT1_CMD: SET_CFUN=1\r\n'), 1500);
      setTimeout(() => this.sendNotification('MCU_TO_CAT1_CMD: READ_IMEI\r\n'), 2000);
      setTimeout(() => this.sendNotification('+SIMEI: 864982050123456\r\n'), 2400);
      setTimeout(() => this.sendNotification('Network Attach: CGREG 1 (Connected to LTE-M)\r\n'), 3000);
      setTimeout(() => this.sendNotification('DNS Resolve: op.foorir.cn -> 120.79.168.21\r\n'), 3600);
      setTimeout(() => this.sendNotification('HTTP POST /api/infrared/V1/dataUpload -> 200 OK\r\n'), 4200);
      setTimeout(() => this.sendNotification('CAT1 Power off\r\n'), 4800);
      return;
    }
  }

  private sendNotification(text: string) {
    if (this.onNotifyCallback) {
      this.onNotifyCallback(new TextEncoder().encode(text));
    }
  }
}
